import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import SignaturePad from './SignaturePad';

const ApprovalActions = ({ approval, onSubmit, submitting }) => {
  const [decision, setDecision] = useState(null); // 'approved' | 'rejected'
  const [comments, setComments] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!decision) newErrors.decision = 'Please select approve or reject';
    if (decision === 'rejected' && !comments?.trim()) {
      newErrors.comments = 'Please provide a reason for rejection';
    }
    if (decision === 'approved') {
      if (!signerName?.trim()) newErrors.signerName = 'Please enter your full name';
      if (!signatureData) newErrors.signature = 'Please provide your digital signature';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit?.({
      decision,
      comments,
      signerName,
      signatureData
    });
  };

  if (approval?.status !== 'pending') {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
        <Icon name="CheckSquare" size={18} color="var(--color-primary)" />
        <h3 className="text-base font-bold text-gray-900">Your Decision</h3>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Decision Buttons */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-3">Do you approve this service?</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => { setDecision('approved'); setErrors({}); }}
              className={`flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                decision === 'approved' ?'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm' :'border-gray-200 bg-white text-gray-600 hover:border-emerald-300 hover:bg-emerald-50/50'
              }`}
            >
              <Icon name="ThumbsUp" size={18} color={decision === 'approved' ? '#059669' : '#6b7280'} />
              Approve
            </button>
            <button
              type="button"
              onClick={() => { setDecision('rejected'); setErrors({}); }}
              className={`flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl border-2 font-semibold text-sm transition-all ${
                decision === 'rejected' ?'border-red-500 bg-red-50 text-red-700 shadow-sm' :'border-gray-200 bg-white text-gray-600 hover:border-red-300 hover:bg-red-50/50'
              }`}
            >
              <Icon name="ThumbsDown" size={18} color={decision === 'rejected' ? '#dc2626' : '#6b7280'} />
              Reject
            </button>
          </div>
          {errors?.decision && (
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />{errors?.decision}
            </p>
          )}
        </div>

        {/* Comments */}
        {decision && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {decision === 'rejected' ? (
                <span>Reason for Rejection <span className="text-red-500">*</span></span>
              ) : (
                'Additional Comments (optional)'
              )}
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e?.target?.value)}
              rows={3}
              placeholder={decision === 'rejected' ?'Please explain why you are rejecting this service request...' :'Any additional notes or instructions for the service team...'
              }
              className={`w-full px-4 py-3 text-sm border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                errors?.comments ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
              }`}
            />
            {errors?.comments && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />{errors?.comments}
              </p>
            )}
          </div>
        )}

        {/* Signature Section - only for approval */}
        {decision === 'approved' && (
          <div className="space-y-4 pt-2 border-t border-gray-100">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e?.target?.value)}
                placeholder="Enter your full legal name"
                className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                  errors?.signerName ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'
                }`}
              />
              {errors?.signerName && (
                <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} />{errors?.signerName}
                </p>
              )}
            </div>

            <SignaturePad
              onSignatureChange={setSignatureData}
              required
            />
            {errors?.signature && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />{errors?.signature}
              </p>
            )}
          </div>
        )}

        {/* Submit Button */}
        {decision && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className={`w-full py-3.5 px-6 rounded-xl font-semibold text-sm text-white transition-all flex items-center justify-center gap-2.5 ${
              decision === 'approved' ?'bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400' :'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
            } disabled:cursor-not-allowed shadow-sm`}
          >
            {submitting ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Icon name={decision === 'approved' ? 'CheckCircle2' : 'XCircle'} size={16} />
                {decision === 'approved' ? 'Confirm Approval' : 'Confirm Rejection'}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ApprovalActions;
